import React, {
  useEffect, useState, useRef, useCallback,
} from 'react';
import {
  makeStyles, createStyles, Theme, InputBase,
} from '@material-ui/core';
import { Search as SearchIcon } from '@material-ui/icons';
import debounce from 'debounce';

import hotKeyConverter from '../../utility/keyCodeConverter';

const useStyles = makeStyles(({ colors, palette }: Theme) => createStyles({
  container: {
    display: 'flex',
    backgroundColor: '#F9FAFB',
    border: `1px solid ${colors.border}`,
    borderRadius: '5px',
    height: 36,
    '&:hover': {
    },
  },
  inputRoot: {
    flexGrow: 1,
    marginLeft: '1rem',
  },
  inputInput: {
    '&::placeholder': {
      color: palette.text.primary,
    },
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '0.5rem',
    paddingRight: '0.5rem',
  },
  icon: {
    width: 20,
    height: 20,
    color: colors.icon,
  },
}), { name: 'SearchBar' });

type Props = {
  value: string;
  onChange: (value: string) => void;
  addHandler?: () => void;
};

const SearchBar = ({ value, onChange, addHandler }: Props) => {
  const classes = useStyles();
  const searchRef = useRef<HTMLInputElement>(null);
  const [canCreate, setCanCreate] = useState(false);

  useEffect(() => {
    const hotKeyHandler = (event: KeyboardEvent) => {
      if (event.keyCode === hotKeyConverter('slash')) {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', hotKeyHandler);
    return () => {
      window.removeEventListener('keydown', hotKeyHandler);
    };
  }, [searchRef]);
  const [searchText, setSearch] = useState(value);
  const [searchFilter, setFilter] = useState(value);
  const setSearchFilter = () => setSearch(searchFilter);
  const searchWithDelay = useCallback(debounce(setSearch, 600), [setSearch]);
  useEffect(() => searchWithDelay(searchFilter), [
    searchFilter,
    searchWithDelay,
  ]);

  useEffect(() => {
    onChange(searchText);
  }, [searchText, onChange]);

  const keyDownHandler = useCallback(({ key }: React.KeyboardEvent) => {
    if (key === 'Escape') {
      setFilter('');
      setSearch('');
    }
    if (key !== 'Enter') {
      setCanCreate(false);
    }
  }, [setFilter, setSearch, setCanCreate]);

  const keyPressHandler = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canCreate && addHandler) {
      addHandler();
    }
  }, [addHandler, canCreate]);

  const localOnChange = useCallback((e) => {
    setCanCreate(false);
    setFilter(e.target.value);
    setTimeout(() => { setCanCreate(true); }, 800);
  }, []);

  return (
    <div className={classes.container}>
      <InputBase
        spellCheck={false}
        placeholder="Search (/)"
        inputRef={searchRef}
        value={searchFilter}
        onChange={(e) => localOnChange(e)}
        onBlur={setSearchFilter}
        onKeyDown={keyDownHandler}
        onKeyPress={keyPressHandler}
        onFocus={() => { setCanCreate(true); }}
        classes={{
          root: classes.inputRoot,
          input: classes.inputInput,
        }}
        inputProps={{ 'aria-label': 'search' }}
      />
      <div className={classes.iconWrapper}>
        <SearchIcon className={classes.icon} />
      </div>
    </div>
  );
};

export default SearchBar;
